/**
 * RenderStateManager.ts
 * 
 * Utilities for managing render state to prevent re-render loops.
 * 
 * REFACTORING_ROADMAP.md Step 10
 * Extracted from EdgeDocsCanvas.V3.tsx
 * 
 * This module provides:
 * - Render guard pattern (prevent concurrent renders)
 * - Render timestamp tracking
 * - Render throttling utilities
 * 
 * @module RenderStateManager
 */

import { MutableRefObject } from 'react';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Refs needed for render state management
 */
export interface RenderStateRefs {
  /** Flag indicating if a render is currently in progress */
  isRenderingRef: MutableRefObject<boolean>;
  /** Timestamp of the last render */
  lastRenderTimestampRef: MutableRefObject<number>;
}

/**
 * Result of attempting to acquire a render lock
 */
export interface RenderLockResult {
  /** Whether the lock was acquired */
  acquired: boolean;
  /** Function to release the lock (call when render completes) */
  release: () => void;
}

/**
 * Options for render throttling
 */
export interface RenderThrottleOptions {
  /** Minimum time between renders in milliseconds */
  minInterval?: number;
  /** Enable debug logging */
  enableLogging?: boolean;
  /** Page index for logging context */
  pageIndex?: number;
}

/**
 * Render trigger tracking for throttling
 */
export interface RenderTriggerState {
  trigger: number;
  sharedTrigger: number;
  timestamp: number;
}

// ============================================================================
// RENDER GUARD PATTERN
// ============================================================================

/**
 * Attempts to acquire a render lock, preventing concurrent renders.
 * 
 * This implements a simple mutex pattern for render operations:
 * - If already rendering, returns { acquired: false }
 * - If not rendering, sets flag and returns { acquired: true, release }
 * 
 * @param refs - Render state refs
 * @param timestamp - Current timestamp for tracking
 * @param options - Optional configuration
 * @returns Lock result with acquired status and release function
 * 
 * @example
 * ```typescript
 * const lock = acquireRenderLock(renderStateRefs, Date.now());
 * if (!lock.acquired) {
 *   debugLog('Already rendering, skipping');
 *   return;
 * }
 * try {
 *   // ... perform render ...
 * } finally {
 *   lock.release();
 * }
 * ```
 */
export function acquireRenderLock(
  refs: RenderStateRefs,
  timestamp: number,
  options: { enableLogging?: boolean; pageIndex?: number } = {}
): RenderLockResult {
  const { enableLogging = false, pageIndex } = options;
  const logPrefix = pageIndex !== undefined ? `[Page ${pageIndex}]` : '';

  // Check if already rendering
  if (refs.isRenderingRef.current) {
    if (enableLogging) {
      console.log(`🎨 ${logPrefix} ⚠️ Already rendering, skipping duplicate call`);
    }
    return {
      acquired: false,
      release: () => {} // No-op release for failed acquisition
    };
  }

  // Acquire lock
  refs.isRenderingRef.current = true;
  refs.lastRenderTimestampRef.current = timestamp;

  if (enableLogging) {
    console.log(`🎨 ${logPrefix} Render lock acquired at ${timestamp}`);
  }

  // Return release function
  return {
    acquired: true,
    release: () => {
      refs.isRenderingRef.current = false;
      if (enableLogging) {
        console.log(`🎨 ${logPrefix} Render lock released`);
      }
    }
  };
}

/**
 * Releases the render lock.
 * 
 * Simple utility for cases where you don't use the lock pattern.
 * 
 * @param refs - Render state refs
 */
export function releaseRenderLock(refs: RenderStateRefs): void {
  refs.isRenderingRef.current = false;
}

/**
 * Checks if a render is currently in progress.
 * 
 * @param refs - Render state refs
 * @returns true if rendering
 */
export function isRendering(refs: RenderStateRefs): boolean {
  return refs.isRenderingRef.current;
}

// ============================================================================
// RENDER THROTTLING
// ============================================================================

/**
 * Default minimum interval between renders (milliseconds)
 */
export const DEFAULT_RENDER_THROTTLE_MS = 16; // ~60fps

/**
 * Checks if enough time has passed since the last render.
 * 
 * @param refs - Render state refs
 * @param currentTimestamp - Current timestamp
 * @param minInterval - Minimum interval between renders (default: 16ms)
 * @returns true if render should proceed
 */
export function shouldRender(
  refs: RenderStateRefs,
  currentTimestamp: number,
  minInterval: number = DEFAULT_RENDER_THROTTLE_MS
): boolean {
  const elapsed = currentTimestamp - refs.lastRenderTimestampRef.current;
  return elapsed >= minInterval;
}

/**
 * Checks if a render trigger has changed since last render.
 * 
 * @param lastState - Previous trigger state
 * @param currentTrigger - Current trigger value
 * @param currentSharedTrigger - Current shared trigger value
 * @returns true if triggers have changed
 */
export function hasRenderTriggerChanged(
  lastState: RenderTriggerState,
  currentTrigger: number,
  currentSharedTrigger: number
): boolean {
  return lastState.trigger !== currentTrigger || 
         lastState.sharedTrigger !== currentSharedTrigger;
}

/**
 * Updates the render trigger state.
 * 
 * @param stateRef - Ref to trigger state
 * @param trigger - New trigger value
 * @param sharedTrigger - New shared trigger value
 * @param timestamp - Current timestamp
 */
export function updateRenderTriggerState(
  stateRef: MutableRefObject<RenderTriggerState>,
  trigger: number,
  sharedTrigger: number,
  timestamp: number
): void {
  stateRef.current = {
    trigger,
    sharedTrigger,
    timestamp
  };
}

// ============================================================================
// RENDER STATE DIAGNOSTICS
// ============================================================================

/**
 * Gets diagnostic information about current render state.
 * 
 * @param refs - Render state refs
 * @returns Diagnostic object for logging
 */
export function getRenderStateDiagnostics(
  refs: RenderStateRefs
): Record<string, unknown> {
  const now = Date.now();
  const lastRender = refs.lastRenderTimestampRef.current;
  const timeSinceLastRender = lastRender > 0 ? now - lastRender : -1;

  return {
    isRendering: refs.isRenderingRef.current,
    lastRenderTimestamp: lastRender,
    timeSinceLastRenderMs: timeSinceLastRender,
    currentTimestamp: now
  };
}

/**
 * Creates initial render trigger state.
 * 
 * @returns Initial trigger state
 */
export function createInitialRenderTriggerState(): RenderTriggerState {
  return {
    trigger: 0,
    sharedTrigger: 0,
    timestamp: 0
  };
}
